import Lenis from 'lenis'
import { useEffect } from 'react'

type LenisScrollListener = (scroll: number) => void

const lenisScrollListeners = new Set<LenisScrollListener>()

/**
 * Subscribe to the Lenis scroll position. Lenis drives the window scroll on
 * pages that render `<SmoothScroller />`, and emits on every position change —
 * including programmatic jumps (e.g. ScrollRestoration scrolling to a hash),
 * which native `scroll` listeners miss under Lenis. Returns an unsubscribe fn.
 */
export function onLenisScroll(listener: LenisScrollListener) {
	lenisScrollListeners.add(listener)
	return () => {
		lenisScrollListeners.delete(listener)
	}
}

export function SmoothScroller() {
	useEffect(() => {
		const lenis = new Lenis({
			duration: 1.2,
			easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
			orientation: 'vertical',
			gestureOrientation: 'vertical',
			smoothWheel: true,
			wheelMultiplier: 1,
			touchMultiplier: 2,
		})

		lenis.on('scroll', () => {
			for (const listener of lenisScrollListeners) listener(lenis.scroll)
		})

		let reqId: number

		function raf(time: number) {
			lenis.raf(time)
			reqId = requestAnimationFrame(raf)
		}

		reqId = requestAnimationFrame(raf)

		return () => {
			cancelAnimationFrame(reqId)
			lenis.destroy()
		}
	}, [])

	return null
}
